import * as Sentry from "@sentry/react";
import { AlertTriangle, ArrowLeft } from "lucide-react";
import { useEffect, useState } from "react";
import {
  Link,
  isRouteErrorResponse,
  useNavigate,
  useRouteError,
} from "react-router-dom";

import { Button } from "@/components/ui/button";

/**
 * App-wide error element for the data router. Renders when any route
 * (or a child component) throws — instead of React Router's developer-
 * facing default screen. Surfaces a polished page with:
 *
 *   · Tone-coded headline (404 routing vs. runtime exception).
 *   · One-line user-facing message.
 *   · Try again / Back / Go to dashboard actions.
 *   · Dev-only collapsible stack trace + the raw error body for fast
 *     in-app debugging without bouncing to devtools.
 *
 * Production builds hide the stack and tone down the technical chrome
 * — what users see is the headline + the actions, plus a subtle
 * "Reference" code if we ever need to trace it on the server side.
 */
export function RouteErrorBoundary() {
  const error = useRouteError();
  const navigate = useNavigate();
  const isDev = import.meta.env.DEV;
  const [showDetails, setShowDetails] = useState(false);

  const { headline, message, status } = describe(error);
  const detail = stringifyError(error);

  // Report only genuine failures: runtime exceptions thrown during render,
  // and 5xx route-response errors. Routine 404/403 responses are expected
  // navigation outcomes, not exceptions, so they stay out of Sentry.
  useEffect(() => {
    if (!isRouteErrorResponse(error) || error.status >= 500) {
      Sentry.captureException(error);
    }
  }, [error]);

  return (
    <div className="flex min-h-[70vh] flex-col items-center justify-center px-4 py-10">
      <div className="flex w-full max-w-xl flex-col items-center gap-5 text-center">
        {/* Tone-coded badge — amber for routing / 4xx, red for
            runtime exceptions / 5xx. Keeps the page readable at
            a glance without a wall of error text. */}
        <div
          className={`flex size-14 items-center justify-center rounded-full ${
            status && status < 500 ? "bg-warning-dim text-warning" : "bg-destructive-dim text-destructive"
          }`}
        >
          <AlertTriangle size={28} />
        </div>

        <div className="flex flex-col gap-1.5">
          <h1
            className="tracking-tight text-2xl font-bold text-foreground"
                      >
            {headline}
          </h1>
          <p className="text-sm text-muted-foreground">{message}</p>
          {status !== undefined && (
            <p className="tabular-nums text-xs text-text-dim">
              Reference: HTTP {status}
            </p>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-center gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft size={12} />
            Back
          </Button>
          <Link to="/dashboard">
            <Button type="button">Go to dashboard</Button>
          </Link>
        </div>

        {isDev && detail && (
          <div className="mt-4 w-full">
            <button
              type="button"
              onClick={() => setShowDetails((v) => !v)}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              {showDetails ? "Hide" : "Show"} technical details
            </button>
            {showDetails && (
              <pre className="scrollbar-thin mt-2 max-h-[40vh] overflow-auto rounded-xl border border-border bg-surface-elevated p-3 text-left tabular-nums text-xs text-muted-foreground">
                {detail}
              </pre>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Maps an unknown error into a user-facing headline + message.
 * Distinguishes route-response errors (404, 500 from a loader/action)
 * from runtime exceptions thrown during render.
 */
function describe(error: unknown): {
  headline: string;
  message: string;
  status?: number;
} {
  if (isRouteErrorResponse(error)) {
    if (error.status === 404) {
      return {
        headline: "Page not found",
        message: "We couldn't find the page you were looking for.",
        status: 404,
      };
    }
    if (error.status === 401 || error.status === 403) {
      return {
        headline: "You don't have access",
        message: "Your account doesn't have permission to view this page.",
        status: error.status,
      };
    }
    return {
      headline: "Something went wrong",
      message:
        error.statusText ||
        "The page failed to load. Try again, or head back to the dashboard.",
      status: error.status,
    };
  }

  if (error instanceof Error) {
    return {
      headline: "Something went wrong",
      message:
        error.message ||
        "An unexpected error occurred while rendering this page.",
    };
  }

  return {
    headline: "Something went wrong",
    message: "An unexpected error occurred while rendering this page.",
  };
}

/** Returns the raw error body suitable for the dev-only details panel. */
function stringifyError(error: unknown): string | null {
  if (isRouteErrorResponse(error)) {
    const body =
      typeof error.data === "string"
        ? error.data
        : JSON.stringify(error.data, null, 2);
    return `HTTP ${error.status} ${error.statusText}\n\n${body}`;
  }
  if (error instanceof Error) {
    return error.stack ?? `${error.name}: ${error.message}`;
  }
  try {
    return JSON.stringify(error, null, 2);
  } catch {
    return String(error);
  }
}
